/**
 * Refers to {@link CustomerInfo} of @revenuecat/purchases-js
 *
 * See details in [revenue-cat docs](https://www.revenuecat.com/docs/customers/customer-info#getting-subscription-status-via-the-sdk)
 */
export interface ClosCustomerInfo {
  entitlements: ClosEntitlement[];

  /**
   * URL tuple to manage the active subscription of the user
   *
   * If there are no active entitlements or the customer hasn't been authenticated, it will be an empty array
   */
  managementURLs: [string] | [];
}

/**
 * Refers to {@link EntitlementInfo} of @revenuecat/purchases-js
 */
export interface ClosEntitlement {
  id: string;

  /** Refers to {@link ClosProduct.id} */
  productId: string;

  isActive: boolean;

  /** Date string */
  expiresAt: string;

  store: { type: ClosStoreType };
}

export interface ClosProduct {
  id: string;

  title: string;

  description: string;

  price: ClosPrice;

  type: ClosProductType.Subscription;

  period: ClosSubscriptionPeriod;

  /**
   * Passthrough of RevenueCat product metadata (custom key/value attributes
   * configured per product in the RevenueCat dashboard). Keys expected today:
   * `tagline`, `description`, `perks` (JSON-encoded `TierPerk[]`).
   *
   * Always an object; empty object when RC returns no metadata.
   */
  metadata: Record<string, string>;
}

export enum ClosProductType {
  /**
   * A product that is an auto-renewing subscription.
   *
   * For now, this is the only product introduced in Clos
   */
  Subscription = 'subscription',
}

export enum ClosSubscriptionPeriod {
  Year = 'year',

  Month = 'month',

  Week = 'week',

  Day = 'day',
}

export interface ClosPrice {
  /** Price in micro-units of the currency. For example, $9.99 is represented as 9990000 */
  micros: number;

  /**
   * Returns ISO 4217 currency code for price.
   * For example, if price is specified in British pounds sterling,
   * currency is "GBP".
   * If currency code cannot be determined, currency symbol is returned.
   */
  readonly currency: string;

  /**
   * Formatted price string including price and currency.
   */
  readonly formattedPrice: string;
}

export interface ClosPurchase {
  product: ClosProduct;
  customerDetails: {
    email: string;
  };
}

export type ClosStoreType =
  | 'app_store'
  | 'mac_app_store'
  | 'play_store'
  | 'amazon'
  | 'stripe'
  | 'rc_billing'
  | 'promotional'
  | 'paddle'
  | 'test_store'
  | 'unknown';
